import React, { useState, useEffect } from 'react';
import axiosInstance from 'utils/axios-instance';
import Swal from 'sweetalert2';

type AdminRequest = {
    id: number;
    communityUuid: string;
    userId: number;
    nickname: string;
    status: 'pending' | 'approved' | 'rejected';
    requestMessage: string;
    responseMessage: string | null;
    processedBy: number | null;
    processedAt: string | null;
    createdAt: string;
}

interface Props {
    communityUuid: string;
}

const AdminRequestManagement: React.FC<Props> = ({ communityUuid }) => {
    const [requests, setRequests] = useState<AdminRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');

    useEffect(() => {
        fetchRequests();
    }, [communityUuid, filterStatus]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const statusParam = filterStatus === 'all' ? '' : `&status=${filterStatus}`;
            const response = await axiosInstance.get(
                `/api/community/admin/requests?communityUuid=${communityUuid}${statusParam}`
            );
            setRequests(response.data);
        } catch (error) {
            console.error('관리자 신청 목록 조회 실패:', error);
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '관리자 신청 목록을 불러오는 중 오류가 발생했습니다.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleProcess = async (requestId: number, nickname: string, action: 'approved' | 'rejected') => {
        const actionText = action === 'approved' ? '승인' : '거절';
        
        const result = await Swal.fire({
            title: `관리자 신청 ${actionText}`,
            text: `${nickname} 님의 관리자 신청을 ${actionText}하시겠습니까?`,
            icon: 'question',
            input: 'textarea',
            inputLabel: '응답 메시지 (선택)',
            inputPlaceholder: `${actionText} 사유나 메시지를 입력하세요...`,
            showCancelButton: true,
            confirmButtonText: actionText,
            cancelButtonText: '취소',
            confirmButtonColor: action === 'approved' ? '#3085d6' : '#d33',
        });

        if (result.isConfirmed) {
            try {
                const response = await axiosInstance.post('/api/community/admin/process', {
                    requestId,
                    communityUuid,
                    status: action,
                    responseMessage: result.value || null
                });

                if (response.data.statusCode === 200) {
                    Swal.fire({
                        icon: 'success',
                        title: '완료',
                        text: response.data.message,
                    });
                    fetchRequests();
                }
            } catch (error: any) {
                console.error('신청 처리 실패:', error);
                Swal.fire({
                    icon: 'error',
                    title: '오류',
                    text: error.response?.data?.message || '신청 처리 중 오류가 발생했습니다.',
                });
            }
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <span className="status-badge pending">대기중</span>;
            case 'approved':
                return <span className="status-badge approved">승인됨</span>;
            case 'rejected':
                return <span className="status-badge rejected">거절됨</span>;
            default:
                return <span className="status-badge">{status}</span>;
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>관리자 신청 목록을 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div className="requests-management">
            <div className="management-header">
                <h2>관리자 신청 관리</h2>
                <div className="filter-buttons">
                    <button 
                        className={`filter-button ${filterStatus === 'pending' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('pending')}
                    >
                        대기중
                    </button>
                    <button 
                        className={`filter-button ${filterStatus === 'approved' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('approved')}
                    >
                        승인됨
                    </button>
                    <button 
                        className={`filter-button ${filterStatus === 'rejected' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('rejected')}
                    >
                        거절됨
                    </button>
                    <button 
                        className={`filter-button ${filterStatus === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('all')}
                    >
                        전체
                    </button>
                </div>
            </div>

            {requests.length === 0 ? (
                <div className="empty-state">
                    <p>{filterStatus === 'pending' ? '대기 중인 신청이 없습니다.' : '신청 내역이 없습니다.'}</p>
                </div>
            ) : (
                <div className="requests-list">
                    {requests.map(request => (
                        <div key={request.id} className="request-card">
                            <div className="request-header">
                                <div className="request-info">
                                    <h3>{request.nickname}</h3>
                                    {getStatusBadge(request.status)}
                                </div>
                                <span className="request-date">
                                    {new Date(request.createdAt).toLocaleString('ko-KR')}
                                </span>
                            </div>

                            <div className="request-content">
                                <div className="message-section">
                                    <h4>신청 메시지</h4>
                                    <p>{request.requestMessage}</p>
                                </div>

                                {request.responseMessage && (
                                    <div className="message-section response">
                                        <h4>응답 메시지</h4>
                                        <p>{request.responseMessage}</p>
                                        {request.processedAt && (
                                            <span className="processed-date">
                                                처리일: {new Date(request.processedAt).toLocaleString('ko-KR')}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {request.status === 'pending' && (
                                <div className="request-actions">
                                    <button 
                                        className="approve-button"
                                        onClick={() => handleProcess(request.id, request.nickname, 'approved')}
                                    >
                                        승인
                                    </button>
                                    <button 
                                        className="reject-button"
                                        onClick={() => handleProcess(request.id, request.nickname, 'rejected')}
                                    >
                                        거절
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminRequestManagement;

